import React, { useEffect, useState } from "react";

import { Container } from "@mui/material";
import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import Button from "@mui/material/Button";
import Select from "react-select";

import Grid from "@mui/material/Grid";

import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";

import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";

import { MobileDateRangePicker } from "@mui/x-date-pickers-pro/MobileDateRangePicker";
import dayjs from "dayjs";

import DrawerAppBar from "../../components/DrawerAppBar";
import Title from "../../components/Title";
import ProgressBar from "../../components/ProgressBar";
import SubTitle from "../../components/SubTitle";
import MyActivitySelector from "../../components/faculty/report/MyActivitySelector";
import StudentsSelector from "../../components/faculty/report/StudentsSelector";

import { doService } from "../../services/ServiceHelper";
import {
  getActivityDates,
  getTeacherRecommendations,
  removeTeacherRecommendation,
  saveTeacherRecommendation,
} from "../../services/ActivityService";
import { getPeriodName } from "../../services/SchoolService";

import {
  StripedTableRow,
  StripedTableCell,
} from "../../components/StripedTable";
import TableRowNoResults from "../../components/TableRowNoResults";

const Recommend = () => {
  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();
  const [showProgress, setShowProgress] = useState(false);

  const [_dateRange, setDateRange] = useState([dayjs(), dayjs()]);
  const [_dialogOpen, setDialogOpen] = useState(false);

  const [_selectedActivityEid, setSelectedActivityEid] = useState("XX");
  const [_allActivityDates, setAllActivityDates] = useState([]);
  const [_selectedActivityDates, setSelectedActivityDates] = useState([]);
  const [_selectedStudentEids, setSelectedStudentEids] = useState([]);

  const [_recommendations, setRecommendations] = useState([]);

  useEffect(() => {
    initRecommendations();
  }, []);

  const initRecommendations = () => {
    setShowProgress(true);
    doService(getTeacherRecommendations(_school, _jwt), (results) => {
      setShowProgress(false);
      setRecommendations(results.data);
    });
  };

  const handleCreate = () => {
    setDialogOpen(true);
  };

  const onChangeActivityEid = (activityEid) => {
    setSelectedActivityEid(activityEid);

    if (activityEid === "XX") {
      setSelectedActivityDates([]);
      return;
    }

    // get activity dates for activity
    setShowProgress(true);
    doService(getActivityDates(_school, _jwt, activityEid), (results) => {
      setShowProgress(false);
      const activityDates = results.data;

      const rows = [];
      for (let activityDate of activityDates) {
        rows.push({
          label: `${dayjs(activityDate.date).format("MM/DD/YYYY")}: Period ${
            activityDate.period
          }`,
          value: activityDate.eid,
        });
      }

      setAllActivityDates(rows);
    });
  };

  const handleChangeActivityDates = (values) => {
    setSelectedActivityDates(values);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleOK = () => {
    let isError = true;

    if (_selectedStudentEids.length === 0) {
      alert("Please select 1 or more students.");
    } else if (_selectedActivityEid === "XX") {
      alert("Please select an Activity.");
    } else if (_selectedActivityDates.length === 0) {
      alert("Please select one or more Activity Dates.");
    } else {
      isError = false;
    }

    if (isError) {
      // leave dialog open
      return;
    }

    const activityDateEids = [];
    for (let activityDate of _selectedActivityDates) {
      activityDateEids.push(activityDate.value);
    }

    const studentEids = [];
    for (let student of _selectedStudentEids) {
      studentEids.push(student.value);
    }

    setShowProgress(true);
    doService(
      saveTeacherRecommendation(
        _school,
        _jwt,
        _selectedActivityEid,
        activityDateEids,
        _dateRange[0].format("YYYY-MM-DD"),
        _dateRange[1].format("YYYY-MM-DD"),
        studentEids
      ),
      (results) => {
        setShowProgress(false);
        setDialogOpen(false);
        initRecommendations();
        window.showSnack("Recommendation Saved.");
      }
    );
  };

  const handleChangeDateRange = (value) => setDateRange(value);

  const onSelectStudents = (studentEids) => {
    setSelectedStudentEids(studentEids);
  };

  const handleRemove = (recommendationId) => {
    if (!window.confirm("Click OK to remove recommendation.")) {
      return;
    }

    setShowProgress(true);
    doService(
      removeTeacherRecommendation(_school, _jwt, recommendationId),
      (results) => {
        setShowProgress(false);
        initRecommendations();
      }
    );
  };

  return (
    <>
      <DrawerAppBar />

      <Container sx={{ mt: 1 }}>
        <Title text="Recommendations to Students" showBack={true} />
        <ProgressBar showProgress={showProgress} />

        <Button sx={{ mt: 1 }} variant="contained" onClick={handleCreate}>
          Create Recommendation
        </Button>

        <Box sx={{ boxShadow: 1, p: 1, mt: 2 }}>
          <SubTitle text="Recommendations" />

          <TableContainer component={Paper} sx={{ mt: 1, mb: 2 }}>
            <Table size="small">
              <TableHead>
                <StripedTableRow>
                  <StripedTableCell>Date/Period</StripedTableCell>
                  <StripedTableCell>Activity</StripedTableCell>
                  <StripedTableCell>Student</StripedTableCell>
                  <StripedTableCell>Date Start / End</StripedTableCell>
                  <StripedTableCell align="center">Actions</StripedTableCell>
                </StripedTableRow>
              </TableHead>
              <TableBody>
                <TableRowNoResults data={_recommendations} />
                {_recommendations.map((result) => (
                  <StripedTableRow key={result.eid}>
                    <StripedTableCell>
                      {dayjs(result.date).format("MM/DD")}
                      <br />
                      {getPeriodName(_school, result.period - 1)}
                    </StripedTableCell>
                    <StripedTableCell>{result.activityName}</StripedTableCell>
                    <StripedTableCell>
                      {result.studentLastName}, {result.studentFirstName}
                    </StripedTableCell>
                    <StripedTableCell>
                      {dayjs(result.dateStart).format("MM/DD")} /{" "}
                      {dayjs(result.dateEnd).format("MM/DD")}
                    </StripedTableCell>
                    <StripedTableCell align="center">
                      <Button
                        variant="outlined"
                        onClick={() => handleRemove(result.id)}
                      >
                        REMOVE
                      </Button>
                    </StripedTableCell>
                  </StripedTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Container>

      <Dialog fullWidth={true} open={_dialogOpen} onClose={handleDialogClose}>
        <DialogTitle sx={{ p: 1 }}>Create Recommendation</DialogTitle>
        <DialogContent sx={{ pb: 0 }}>
          <Grid container spacing={1} sx={{ pt: 1 }}>
            <Grid sx={{ pt: "0.5em !important", mb: 1 }} item xs={12} md={12}>
              <StudentsSelector
                school={_school}
                jwt={_jwt}
                onChange={onSelectStudents}
                selectedStudentEids={_selectedStudentEids}
              />
            </Grid>{" "}
            <Grid sx={{ pt: "0.5em !important", mb: 1 }} item xs={6} md={6}>
              <MyActivitySelector
                school={_school}
                jwt={_jwt}
                onChange={onChangeActivityEid}
              />
            </Grid>
            <Grid sx={{ pt: "0.5em !important", mb: 1 }} item xs={6} md={6}>
              <InputLabel>Activity Dates (select Activity first): *</InputLabel>
              <Select
                options={_allActivityDates}
                onChange={handleChangeActivityDates}
                value={_selectedActivityDates}
                isMulti
              />
            </Grid>
            <Grid sx={{ pt: "0.5em !important", mb: 1 }} item xs={12} md={12}>
              <InputLabel>When to Show: *</InputLabel>
              <MobileDateRangePicker
                defaultValue={[dayjs(), dayjs()]}
                value={_dateRange}
                sx={{ mt: 1, mb: 0 }}
                onChange={handleChangeDateRange}
              />
            </Grid>
          </Grid>
          <br />
          <br />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} variant="outlined">Cancel</Button>
          <Button variant="contained" onClick={handleOK}>
            OK
          </Button>
        </DialogActions>{" "}
      </Dialog>
    </>
  );
};

export default Recommend;
